import { DateStruct, DepartureRange } from "../site-types";
import { format, parse } from "date-fns";
import { nlBE, fr, enGB } from "date-fns/locale";

const getLocale = (language: string) => {
  switch (language) {
    case "nl-BE":
      return nlBE;
    case "fr-BE":
      return fr;
    default:
      return enGB;
  }
};

// new Date("yyyy-MM-dd") parses as UTC midnight, so local getters shift the day
// back in timezones west of Greenwich (e.g. Costa Rica). Date-only strings must
// be parsed as local time instead.
export const parseLocalDate = (value: string): Date => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return parse(value, "yyyy-MM-dd", new Date());
  }
  return new Date(value);
};

export const dateToDateStruct = (date: Date | undefined): DateStruct => {
  if (date === undefined) {
    return { year: 0, month: 0, day: 0 };
  }
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1, // Months are 0-based in JS
    day: date.getDate(),
  };
};

export const timeFromDateTime = (dateTime: Date | undefined): string => {
  if (!dateTime) {
    return "";
  }
  const date = new Date(dateTime);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  return `${hours}:${minutes < 10 ? "0" : ""}${minutes}`;
};

export const longFormatDate = (dateTime: Date | undefined, language: string): string => {
  if (!dateTime) {
    return "";
  }
  const locale = getLocale(language);
  const formattedDate = format(new Date(dateTime), "eeeeee. dd MMM yy", { locale });
  return formattedDate;
};

export const dateFromDateTime = (dateTime: Date | undefined, language: string): string => {
  if (!dateTime) {
    return "";
  }
  const locale = getLocale(language);
  const formattedDate = format(new Date(dateTime), "dd MMM yyyy", { locale });
  return formattedDate;
};

export const durationTicksInHoursString = (durationInTicks: number, hoursText: string): string => {
  const totalMinutes = Math.floor(durationInTicks / 10_000 / 1000 / 60);
  return minutesToHoursString(totalMinutes, hoursText);
};

export const durationInTicksInMinutes = (durationInTicks: number): number => {
  const totalMinutes = Math.floor(durationInTicks / 10_000 / 1000 / 60);
  return totalMinutes;
};

export const minutesToHoursString = (totalMinutes: number, hoursText: string): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const paddedMinutes = minutes.toString().padStart(2, "0");

  return hours.toString() + hoursText + paddedMinutes;
};

export const rangeFromDateTimeInMinutes = (dateTime: Date | undefined): DepartureRange => {
  if (!dateTime) {
    return 0;
  }
  const date = new Date(dateTime);
  const hours = date.getHours();
  const minutes = hours * 60 + date.getMinutes();
  switch (true) {
    case minutes >= 300 && minutes < 720:
      return DepartureRange.Morning;
    case minutes >= 720 && minutes < 1080:
      return DepartureRange.Afternoon;
    case minutes >= 1080 && minutes < 1440:
      return DepartureRange.Evening;
    default:
      return DepartureRange.Night;
  }
};

export const dateToString = (date: Date | undefined | null, formatString: string): string => {
  if (!date) {
    return "";
  }

  return format(new Date(date), formatString);
};
